
import { GoogleGenAI, Type, Modality } from "@google/genai";
import {
  ReadingPassage,
  ListeningScript,
  ChartData,
  EssayPrompt,
  Enhancement,
  LogicPuzzle,
  CueCard,
  WordFamily,
  Direction,
  GrammarChallenge
} from "../types";

// Initialize the API
const ai = new GoogleGenAI({ apiKey: import.meta.env.VITE_GEMINI_API_KEY });

// Helper to clean JSON output from LLM
const cleanJson = (text: string): string => {
  if (!text) return '{}';
  let cleaned = text.trim();
  // Remove markdown code blocks if present
  if (cleaned.startsWith('```json')) {
    cleaned = cleaned.replace(/^```json\s*/, '').replace(/\s*```$/, '');
  } else if (cleaned.startsWith('```')) {
    cleaned = cleaned.replace(/^```\s*/, '').replace(/\s*```$/, '');
  }
  return cleaned;
};

/**
 * THE INFINITY ENGINE
 * Handles all AI generation tasks for the Warfare System
 */
export const geminiEngine = {

  async execute<T>(prompt: string, schema: any, retries = 5): Promise<T> {
    let lastError: any;

    for (let i = 0; i < retries; i++) {
      try {
        const response = await ai.models.generateContent({
          model: 'gemini-3-flash-preview',
          contents: [{ role: 'user', parts: [{ text: prompt }] }],
          config: {
            responseMimeType: "application/json",
            responseSchema: schema,
            temperature: 0.7,
          }
        });

        // Use response.text property directly
        const text = response.text;
        if (!text) throw new Error("Empty response from Gemini");

        return JSON.parse(cleanJson(text)) as T;
      } catch (error: any) {
        lastError = error;

        // Check for Rate Limit (429) or Service Unavailable (503)
        const isRateLimit = error.message?.includes('429') || error.status === 429 || error.code === 429;
        const isServerOverload = error.message?.includes('503') || error.status === 503 || error.code === 503;
        const isJsonError = error instanceof SyntaxError;

        if (isRateLimit || isServerOverload || isJsonError) {
          // Parse wait time if available
          const match = error.message?.match(/retry in (\d+(\.\d+)?)s/);
          let delay = 0;

          if (match && match[1]) {
            // Parse the exact seconds provided by the API and add a 2-second safety buffer
            delay = Math.ceil(parseFloat(match[1])) * 1000 + 2000;
            console.warn(`⏳ API Rate Limit (Attempt ${i + 1}/${retries}). Waiting ${Math.round(delay / 1000)}s...`);
          } else {
            // Fallback exponential backoff + jitter: 2s, 4s, 8s, 16s...
            delay = Math.pow(2, i + 1) * 1000 + (Math.random() * 1000);
            console.warn(`⚠️ API Error (${isJsonError ? 'JSON' : 'Network'}) (Attempt ${i + 1}/${retries}). Retrying in ${Math.round(delay / 1000)}s...`);
          }

          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }

        // If it's not a retriable error, throw immediately
        throw error;
      }
    }

    console.error(`❌ Max retries (${retries}) reached for Gemini API request.`);
    throw lastError;
  },

  async generateSpeech(text: string): Promise<string> {
    // Correctly using Modality.AUDIO from @google/genai
    const response = await ai.models.generateContent({
      model: "gemini-2.5-flash-preview-tts",
      contents: [{ parts: [{ text: `Say this naturally: ${text}` }] }],
      config: {
        responseModalities: [Modality.AUDIO],
        speechConfig: {
          voiceConfig: {
            prebuiltVoiceConfig: { voiceName: 'Puck' },
          },
        },
      },
    });
    const base64Audio = response.candidates?.[0]?.content?.parts?.[0]?.inlineData?.data;
    if (!base64Audio) throw new Error("No audio data returned");
    return base64Audio;
  },

  async generateReadingPassage(topic: string = "academic", difficulty: string = "medium"): Promise<ReadingPassage> {
    const schema = {
      type: Type.OBJECT,
      properties: {
        title: { type: Type.STRING },
        paragraphs: { type: Type.ARRAY, items: { type: Type.STRING } },
        difficulty: { type: Type.STRING },
        wordCount: { type: Type.NUMBER },
        keyVocab: {
          type: Type.ARRAY,
          items: {
            type: Type.OBJECT,
            properties: {
              word: { type: Type.STRING },
              definition: { type: Type.STRING },
              context: { type: Type.STRING }
            }
          }
        },
        questions: {
          type: Type.ARRAY,
          items: {
            type: Type.OBJECT,
            properties: {
              question: { type: Type.STRING },
              options: { type: Type.ARRAY, items: { type: Type.STRING } },
              answer: { type: Type.STRING }
            }
          }
        }
      }
    };
    return geminiEngine.execute<ReadingPassage>(
      `Generate a high-quality IELTS reading passage about ${topic} with a ${difficulty} difficulty. Ensure academic tone and complex vocabulary.`,
      schema
    );
  },

  async generateLogicPuzzle(difficulty: string = "medium"): Promise<LogicPuzzle> {
    const schema = {
      type: Type.OBJECT,
      properties: {
        passage: { type: Type.STRING },
        statements: {
          type: Type.ARRAY,
          items: {
            type: Type.OBJECT,
            properties: {
              id: { type: Type.NUMBER },
              statement: { type: Type.STRING },
              correctAnswer: { type: Type.STRING },
              explanation: { type: Type.STRING }
            }
          }
        }
      }
    };
    return geminiEngine.execute<LogicPuzzle>(
      `Generate an IELTS Reading Logic Matrix (T/F/NG) with a ${difficulty} difficulty based on a short academic passage.`,
      schema
    );
  },

  async generateListeningScript(scenario: string = "academic lecture"): Promise<ListeningScript> {
    const schema = {
      type: Type.OBJECT,
      properties: {
        scenario: { type: Type.STRING },
        audioText: { type: Type.STRING },
        questions: {
          type: Type.ARRAY,
          items: {
            type: Type.OBJECT,
            properties: {
              question: { type: Type.STRING },
              options: { type: Type.ARRAY, items: { type: Type.STRING } },
              answer: { type: Type.STRING }
            }
          }
        },
        traps: {
          type: Type.ARRAY,
          items: {
            type: Type.OBJECT,
            properties: {
              text: { type: Type.STRING },
              explanation: { type: Type.STRING }
            }
          }
        }
      }
    };
    return geminiEngine.execute<ListeningScript>(
      `Create an IELTS listening script for a ${scenario}. Include typical distractions and traps.`,
      schema
    );
  },

  async generateDirectionalSequence(length: number = 10): Promise<Direction[]> {
    const directions: Direction[] = ['NORTH', 'EAST', 'SOUTH', 'WEST'];
    return Array.from({ length }, () => directions[Math.floor(Math.random() * directions.length)]);
  },

  async generateChartData(): Promise<ChartData> {
    const schema = {
      type: Type.OBJECT,
      properties: {
        type: { type: Type.STRING },
        data: {
          type: Type.ARRAY,
          items: {
            type: Type.OBJECT,
            properties: { label: { type: Type.STRING }, value: { type: Type.NUMBER } }
          }
        },
        description: { type: Type.STRING },
        keyTrends: { type: Type.ARRAY, items: { type: Type.STRING } }
      }
    };
    return geminiEngine.execute<ChartData>(
      "Generate realistic IELTS Writing T1 data (e.g., energy usage, population trends).",
      schema
    );
  },

  async generateEssayPrompt(): Promise<EssayPrompt> {
    const schema = {
      type: Type.OBJECT,
      properties: {
        question: { type: Type.STRING },
        type: { type: Type.STRING },
        context: { type: Type.STRING },
        keyWords: { type: Type.ARRAY, items: { type: Type.STRING } },
        sampleIdeas: { type: Type.ARRAY, items: { type: Type.STRING } }
      }
    };
    return geminiEngine.execute<EssayPrompt>(
      "Create a complex IELTS Task 2 essay prompt on a controversial modern topic.",
      schema
    );
  },

  async evaluateTask1(description: string, userResponse: string): Promise<any> {
    const schema = {
      type: Type.OBJECT,
      properties: {
        bandScore: { type: Type.NUMBER },
        criteria: {
          type: Type.OBJECT,
          properties: {
            taskAchievement: { type: Type.NUMBER, description: "Score from 0 to 9" },
            coherenceCohesion: { type: Type.NUMBER, description: "Score from 0 to 9" },
            lexicalResource: { type: Type.NUMBER, description: "Score from 0 to 9" },
            grammaticalRange: { type: Type.NUMBER, description: "Score from 0 to 9" },
          }
        },
        feedback: { type: Type.STRING },
        suggestions: { type: Type.ARRAY, items: { type: Type.STRING } }
      }
    };
    return geminiEngine.execute<any>(
      `Evaluate this IELTS Writing Task 1 response based on the provided chart data.
      Chart Description: ${description}
      User Response: ${userResponse}
      Provide a realistic band score (0-9) and numerical scores (0-9) for each criterion, along with detailed qualitative feedback.`,
      schema
    );
  },

  async evaluateEssay(question: string, userResponse: string): Promise<any> {
    const schema = {
      type: Type.OBJECT,
      properties: {
        bandScore: { type: Type.NUMBER },
        criteria: {
          type: Type.OBJECT,
          properties: {
            taskResponse: { type: Type.NUMBER },
            coherenceCohesion: { type: Type.NUMBER },
            lexicalResource: { type: Type.NUMBER },
            grammaticalRange: { type: Type.NUMBER },
          }
        },
        feedback: { type: Type.STRING },
        suggestions: { type: Type.ARRAY, items: { type: Type.STRING } }
      }
    };
    return geminiEngine.execute<any>(
      `Evaluate this IELTS Writing Task 2 essay.
      Question: ${question}
      Essay: ${userResponse}
      Provide a realistic band score (0-9) and numerical scores (0-9) for each criterion, along with detailed qualitative feedback.`,
      schema
    );
  },

  async enhanceStatement(input: string): Promise<Enhancement> {
    const schema = {
      type: Type.OBJECT,
      properties: {
        original: { type: Type.STRING },
        enhanced: { type: Type.STRING },
        improvements: { type: Type.ARRAY, items: { type: Type.STRING } },
        bandScore: { type: Type.NUMBER }
      }
    };

    // We reuse execute for consistent error handling logic
    return geminiEngine.execute<Enhancement>(
      `Transform this IELTS speaking statement to Band 9: "${input}". Provide detailed analysis.`,
      schema
    );
  },

  async generateCueCard(): Promise<CueCard> {
    const schema = {
      type: Type.OBJECT,
      properties: {
        topic: { type: Type.STRING },
        bulletPoints: { type: Type.ARRAY, items: { type: Type.STRING } },
        followUp: { type: Type.ARRAY, items: { type: Type.STRING } }
      }
    };
    return geminiEngine.execute<CueCard>(
      "Generate a realistic IELTS Speaking Part 2 cue card topic with guidance points and follow up questions.",
      schema
    );
  },

  async generateWordFamily(root: string): Promise<WordFamily> {
    const schema = {
      type: Type.OBJECT,
      properties: {
        root: { type: Type.STRING },
        derivatives: {
          type: Type.ARRAY,
          items: {
            type: Type.OBJECT,
            properties: { word: { type: Type.STRING }, type: { type: Type.STRING } }
          }
        },
        mnemonic: { type: Type.STRING },
        location: { type: Type.STRING }
      }
    };
    return geminiEngine.execute<WordFamily>(
      `Generate a word family for "${root}". Include an immersive visual mnemonic and a specific memory palace room.`,
      schema
    );
  },

  async generateQuickDrill(count: number = 5, complexity: number = 3): Promise<any> {
    const schema = {
      type: Type.OBJECT,
      properties: {
        topic: { type: Type.STRING },
        questions: {
          type: Type.ARRAY,
          items: {
            type: Type.OBJECT,
            properties: {
              q: { type: Type.STRING },
              a: { type: Type.STRING }
            }
          }
        }
      }
    };
    return geminiEngine.execute<any>(
      `Generate ${count} quick logic drills with complexity level ${complexity}.`,
      schema
    );
  },

  async generateVocabularySet(topic: string): Promise<any> {
    const schema = {
      type: Type.OBJECT,
      properties: {
        topic: { type: Type.STRING },
        words: {
          type: Type.ARRAY,
          items: {
            type: Type.OBJECT,
            properties: {
              word: { type: Type.STRING },
              def: { type: Type.STRING },
              ex: { type: Type.STRING }
            }
          }
        }
      }
    };
    return geminiEngine.execute<any>(
      `Generate 10 advanced IELTS vocabulary words related to ${topic}.`,
      schema
    );
  },

  async generateGrammarChallenge(difficulty: string = "medium"): Promise<GrammarChallenge> {
    const schema = {
      type: Type.OBJECT,
      properties: {
        sentence: { type: Type.STRING },
        errorType: { type: Type.STRING },
        options: { type: Type.ARRAY, items: { type: Type.STRING } },
        correctAnswer: { type: Type.STRING },
        explanation: { type: Type.STRING },
        difficulty: { type: Type.STRING }
      }
    };
    return geminiEngine.execute<GrammarChallenge>(
      `Generate an IELTS grammar challenge (${difficulty}). Provide a sentence with a specific grammatical error, and 4 options for the corrected version.`,
      schema
    );
  }
};
